import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Trophy, Target, Users, Gift, CheckCircle, 
  Package, MapPin, Phone, User, ExternalLink, Check, Loader2
} from 'lucide-react';
import { useAdminMissions } from '@/hooks/useAdminMissions';

const AdminMissionsOverview = () => {
  const { loading, stats, mission, pendingShipments, markAsSent } = useAdminMissions();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-20 w-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Section */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-luxury-gold/30 border border-luxury-gold/30">
            <Trophy className="w-6 h-6 text-luxury-gold" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">
              Gerenciamento de Missões
            </h1>
            <p className="text-sm text-muted-foreground">
              Acompanhe o progresso dos usuários e gerencie recompensas
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-900/30 border border-purple-500/30">
                  <Target className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Missões Ativas</p>
                  <p className="text-2xl font-bold text-white">{stats.activeMissions}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-900/30 border border-green-500/30">
                  <Users className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Participantes</p>
                  <p className="text-2xl font-bold text-white">{stats.totalParticipants}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-luxury-gold/30 border border-luxury-gold/30">
                  <CheckCircle className="w-5 h-5 text-luxury-gold" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completaram</p>
                  <p className="text-2xl font-bold text-white">{stats.completed}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-luxury-red/30 border border-luxury-red/30">
                  <Package className="w-5 h-5 text-luxury-red" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pendentes Envio</p>
                  <p className="text-2xl font-bold text-white">{stats.pendingShipments}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mission Details Card */}
        {mission && (
          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-luxury-gold" />
                  {mission.title}
                </CardTitle>
                <Badge className="bg-green-900/40 border-green-500/40 text-green-200">
                  Ativa
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {mission.description}
              </p>
              
              {/* Mission Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-black/30 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Duração</p>
                  <p className="text-white font-medium">{mission.total_days} dias</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Iniciaram</p>
                  <p className="text-white font-medium">{mission.started}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Concluíram</p>
                  <p className="text-white font-medium">{mission.completed}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Taxa</p>
                  <p className="text-white font-medium">{mission.completionRate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reward Shipments Section */}
        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-luxury-red" />
              Recompensas Pendentes de Envio
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingShipments.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-muted-foreground text-sm">
                  Nenhuma recompensa pendente de envio
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingShipments.map((shipment) => (
                  <div key={shipment.id} className="p-4 bg-black/30 rounded-lg border border-white/10 space-y-3">
                    {/* User Info */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-luxury-gold/20 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-luxury-gold" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{shipment.username}</p>
                          <p className="text-xs text-muted-foreground">
                            Completou há {shipment.daysAgo} dias
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-purple-900/40 border-purple-500/40 text-purple-200">
                        Pendente
                      </Badge>
                    </div>

                    {/* Shipping Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-black/20 rounded-lg text-sm">
                      <div className="flex items-start gap-2">
                        <User className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Nome</p>
                          <p className="text-white">{shipment.full_name}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Telefone</p>
                          <p className="text-white">{shipment.phone}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2 sm:col-span-2">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">Endereço</p>
                          <p className="text-white">{shipment.address}, {shipment.number}</p>
                          {shipment.complement && (
                            <p className="text-white/60 text-xs">{shipment.complement}</p>
                          )}
                          <p className="text-white/60 text-xs">CEP: {shipment.zip_code}</p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        onClick={() => markAsSent(shipment.id)}
                        className="flex-1 bg-green-900/30 hover:bg-green-900/50 border border-green-500/30 text-green-200"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Marcar como Enviado
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/main/joia/admin/users?id=${shipment.user_id}`, '_blank')}
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default AdminMissionsOverview;
